import {
  IconChartBar,
  IconChartPie,
  IconChevronDown,
  IconChevronsLeft,
  IconChevronsRight,
  IconGauge,
  IconLogout,
  IconZoomScan,
} from '@tabler/icons-react'
import * as React from 'react'
import { Link, useLocation } from 'react-router-dom'

import ScalrLogo from '@/assets/images/scalr_main_logo.svg'
import ScalrLogoSmall from '@/assets/images/scalr_small_logo.svg'
import { BetaLabel } from '@/components/customComponents/BetaLabel'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { logout as logoutAction, useAppDispatch } from '@/store'
import { cn } from '@/utils'
import { useNavigate } from 'react-router-dom'

interface SidebarChild {
  title: string
  url: string
  isBeta?: boolean
}

interface SidebarItem {
  title: string
  icon: React.ComponentType<{ className?: string }>
  url?: string
  children?: SidebarChild[]
}

const sidebarNav: SidebarItem[] = [
  // {
  //   title: 'Home',
  //   icon: IconLayoutDashboard,
  //   url: '/home',
  // },
  {
    title: 'Clarity',
    icon: IconGauge,
    children: [
      { title: 'Sales', url: '/clarity/sales' },
      { title: 'Labor', url: '/clarity/labor' },
      { title: 'Speed of Service', url: '/clarity/speed-of-service' },
      { title: 'Loyalty', url: '/clarity/loyalty' },
      { title: 'Profit & Loss', url: '/clarity/profit-and-loss' },
      { title: 'Spend', url: '/clarity/spend' },
    ],
  },
  {
    title: 'Clarity Lens',
    icon: IconZoomScan,
    children: [
      { title: 'Sales Lens', url: '/clarity-lens/sales-lens' },
      { title: 'Spend Lens', url: '/clarity-lens/spend-lens' },
      { title: 'Labor Lens', url: '/clarity-lens/labor-lens', isBeta: true },
      {
        title: 'Profit & Loss Lens',
        url: '/clarity-lens/pnl-lens',
        isBeta: true,
      },
    ],
  },
  {
    title: 'Targets',
    icon: IconChartBar,
    children: [
      { title: 'KPI Payout', url: '/targets/kpi-payout', isBeta: true },
      { title: 'KPI Goal Plan', url: '/targets/goal-plans' },
    ],
  },
  // {
  //   title: 'Scalr Base',
  //   icon: IconDatabase,
  //   children: [
  //     { title: 'Product Mix', url: '/scalr-base/product-mix', isBeta: true },
  //     { title: 'Ingredients', url: '/scalr-base/ingredients', isBeta: true },
  //     { title: 'Master Labor', url: '/scalr-base/master-labor', isBeta: true },
  //   ],
  // },
  {
    title: 'Core Suite',
    icon: IconChartPie,
    children: [
      { title: 'Bank Lines', url: '/core-suite/bank-lines', isBeta: true },
      {
        title: 'Bank Match Ext.',
        url: '/core-suite/bank-match-ext',
        isBeta: true,
      },
    ],
  },
]

const bottomNav: SidebarItem[] = [
  // {
  //   title: 'Help Center',
  //   icon: IconHelpCircle,
  //   url: '/help',
  // },
  // {
  //   title: 'Settings',
  //   icon: IconSettings,
  //   url: '/settings',
  // },
]

interface MainSidebarProps {
  isCollapsed: boolean
  onToggle: () => void
}

export function MainSidebar({ isCollapsed, onToggle }: MainSidebarProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [openGroups, setOpenGroups] = React.useState<string[]>([])

  const handleLogout = () => {
    dispatch(logoutAction())
      .unwrap()
      .then(() => {
        navigate('/login')
      })
      .catch(() => {
        navigate('/login')
      })
  }

  const toggleGroup = (title: string) => {
    setOpenGroups((prev) =>
      prev.includes(title) ? prev.filter((t) => t !== title) : [...prev, title]
    )
  }

  return (
    <TooltipProvider>
      <div
        className={cn(
          'flex flex-col h-screen bg-white-50 transition-all duration-300',
          isCollapsed ? 'w-[72px]' : 'w-[272px]'
        )}
      >
        {/* Header */}
        <div className="flex flex-col gap-4">
          <div
            className={cn(
              'flex h-20 border-b border-gray-200',
              isCollapsed
                ? 'flex-col items-center justify-center py-2'
                : 'items-center px-4'
            )}
          >
            <Link
              // to="/home"
              to="/clarity/sales"
              className={cn(
                'flex items-center gap-2 transition-all',
                isCollapsed && 'justify-center'
              )}
            >
              {isCollapsed ? (
                <img src={ScalrLogoSmall} alt="Scalr" className="h-8 w-31" />
              ) : (
                <img src={ScalrLogo} alt="Scalr" className="h-8 w-31" />
              )}
            </Link>
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                'rounded-lg bg-black-50 hover:bg-white-200',
                isCollapsed ? 'mt-2' : 'ml-auto'
              )}
              onClick={onToggle}
            >
              {isCollapsed ? (
                <IconChevronsRight className="h-4 w-4 text-gray-600" />
              ) : (
                <IconChevronsLeft className="h-4 w-4 text-gray-600" />
              )}
            </Button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-4 flex flex-col gap-2">
          {sidebarNav.map((item) => {
            const isParentActive =
              isCollapsed &&
              item.children?.some((child) => child.url === location.pathname)

            return (
              <div key={item.title}>
                {item.children ? (
                  <div>
                    {isCollapsed ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => toggleGroup(item.title)}
                            className={cn(
                              'w-full flex items-center justify-between p-2 text-deepNavy-700 rounded-lg',
                              isCollapsed && 'justify-center px-2',
                              isParentActive && 'bg-blue-500'
                            )}
                          >
                            <div className="flex items-center gap-3">
                              <item.icon
                                className={cn(
                                  'w-5 h-5 shrink-0',
                                  isParentActive
                                    ? 'text-white-50'
                                    : 'text-deepNavy-700'
                                )}
                              />
                            </div>
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="right">
                          <p>{item.title}</p>
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      <button
                        onClick={() => toggleGroup(item.title)}
                        className={cn(
                          'w-full flex items-center justify-between p-2 text-deepNavy-700 rounded-lg',
                          isCollapsed && 'justify-center px-2',
                          isParentActive && 'bg-blue-500'
                        )}
                      >
                        <div className="flex items-center gap-3">
                          <item.icon
                            className={cn(
                              'w-5 h-5 shrink-0',
                              isParentActive
                                ? 'text-white-50'
                                : 'text-deepNavy-700'
                            )}
                          />
                          {!isCollapsed && (
                            <span
                              className={cn(
                                'text-text-m',
                                isParentActive && 'text-white-50'
                              )}
                            >
                              {item.title}
                            </span>
                          )}
                        </div>
                        {!isCollapsed && (
                          <IconChevronDown
                            className={cn(
                              'w-4 h-4 text-deepNavy-700 transition-transform',
                              openGroups.includes(item.title) && 'rotate-180'
                            )}
                          />
                        )}
                      </button>
                    )}
                    {!isCollapsed && openGroups.includes(item.title) && (
                      <div className="mt-1 space-y-0.5">
                        {item.children.map((child) => (
                          <Link
                            key={child.title}
                            to={child.url}
                            className={cn(
                              'flex items-center justify-between p-2 pl-10 rounded-lg group',
                              location.pathname === child.url
                                ? 'bg-blue-500 text-white-50 text-text-m'
                                : 'text-deepNavy-700 text-text-m hover:bg-blue-500 hover:text-white-50'
                            )}
                          >
                            <span
                              className={cn(
                                'text-text-m',
                                location.pathname === child.url
                                  ? 'text-white-50'
                                  : 'text-deepNavy-700 group-hover:text-white-50'
                              )}
                            >
                              {child.title}
                            </span>
                            {child.isBeta && (
                              <BetaLabel size="xs" className="ml-auto" />
                            )}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    {isCollapsed ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Link
                            to={item.url!}
                            className={cn(
                              'flex items-center gap-4 px-2 py-2 rounded-lg',
                              location.pathname === item.url
                                ? 'bg-blue-500 text-white-50 text-text-m'
                                : 'text-deepNavy-700 text-text-m',
                              isCollapsed && 'justify-center px-2'
                            )}
                          >
                            <item.icon
                              className={cn(
                                'w-5 h-5 shrink-0',
                                location.pathname === item.url
                                  ? 'text-white-50'
                                  : 'text-deepNavy-700'
                              )}
                            />
                          </Link>
                        </TooltipTrigger>
                        <TooltipContent side="right">
                          <p>{item.title}</p>
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      <Link
                        to={item.url!}
                        className={cn(
                          'flex items-center gap-4 px-2 py-2 rounded-lg',
                          location.pathname === item.url
                            ? 'bg-blue-500 text-white-50 text-text-m'
                            : 'text-deepNavy-700 text-text-m',
                          isCollapsed && 'justify-center px-2'
                        )}
                      >
                        <item.icon
                          className={cn(
                            'w-5 h-5 shrink-0',
                            location.pathname === item.url
                              ? 'text-white-50'
                              : 'text-deepNavy-700'
                          )}
                        />
                        {!isCollapsed && (
                          <span
                            className={cn(
                              'text-text-m',
                              location.pathname === item.url
                                ? 'text-white-50'
                                : 'text-deepNavy-700'
                            )}
                          >
                            {item.title}
                          </span>
                        )}
                      </Link>
                    )}
                  </>
                )}
              </div>
            )
          })}
        </nav>

        {/* Bottom Navigation */}
        <div>
          <div className="p-4 flex flex-col gap-2">
            {bottomNav.map((item) => (
              <div key={item.title}>
                {isCollapsed ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Link
                        to={item.url!}
                        className={cn(
                          'flex items-center gap-4 p-2 rounded-lg text-text-m',
                          location.pathname === item.url
                            ? 'bg-blue-500 text-white-50'
                            : 'text-deepNavy-700',
                          isCollapsed && 'justify-center px-2'
                        )}
                      >
                        <item.icon
                          className={cn(
                            'w-5 h-5 shrink-0',
                            location.pathname === item.url
                              ? 'text-white-50'
                              : 'text-deepNavy-700'
                          )}
                        />
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      <p>{item.title}</p>
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  <Link
                    to={item.url!}
                    className={cn(
                      'flex items-center gap-4 p-2 rounded-lg text-text-m',
                      location.pathname === item.url
                        ? 'bg-blue-500 text-white-50'
                        : 'text-deepNavy-700',
                      isCollapsed && 'justify-center px-2'
                    )}
                  >
                    <item.icon
                      className={cn(
                        'w-5 h-5 shrink-0',
                        location.pathname === item.url
                          ? 'text-white-50'
                          : 'text-deepNavy-700'
                      )}
                    />
                    {!isCollapsed && (
                      <span
                        className={cn(
                          'text-text-m',
                          location.pathname === item.url
                            ? 'text-white-50 text-text-m'
                            : 'text-deepNavy-700 text-text-m'
                        )}
                      >
                        {item.title}
                      </span>
                    )}
                  </Link>
                )}
              </div>
            ))}
            {!isCollapsed ? (
              <Button
                onClick={handleLogout}
                variant="destructive"
                className="flex items-center gap-2"
              >
                <IconLogout className="w-5 h-5" />
                Logout
              </Button>
            ) : (
              <div className="flex items-center justify-center">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={handleLogout}
                      className="w-8 h-8 hover:bg-red-500 rounded-lg"
                    >
                      <IconLogout className="w-5 h-5 shrink-0" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    <p>Logout</p>
                  </TooltipContent>
                </Tooltip>
              </div>
            )}
          </div>

          {/* {!isCollapsed && (
            <div className="border-t border-gray-100 p-4">
              <button className="w-full flex items-center justify-between gap-3 rounded-xl bg-white p-2 border shadow-sm hover:bg-gray-50">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <div className="h-9 w-9 rounded-full bg-gray-100 flex items-center justify-center">
                      <User className="w-5 h-5 text-gray-600" />
                    </div>
                    <div className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full bg-green-500 ring-2 ring-white" />
                  </div>
                  <span className="font-medium text-deepNavy-700">John Doe</span>
                </div>
                <MoreVertical className="w-5 h-5 text-gray-400" />
              </button>
            </div>
          )} */}
        </div>
      </div>
    </TooltipProvider>
  )
}
